import { useState } from "react";
import { useSession } from "@/frontend/auth-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import {
  Loader2,
  Key,
  Search,
  AlertTriangle,
  Clock,
  Copy,
  Check
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/frontend/components/ui/dialog";
import { Field, FieldLabel, FieldGroup } from "@/frontend/components/ui/field";
import { cn } from "@/frontend/lib/utils";
import { 
  type KeyStatus, 
  getApiKeyStatus, 
  formatKeyDateTime, 
  formatKeyRelativeTime, 
  isKeyExpiringSoon, 
  getApiKeyStatusConfig 
} from "@/frontend/utils/api-keys";
import {
  useActiveMemberQuery,
  useApiKeysQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
} from "@/frontend/mutations/dashboard/settings";

/**
 * Settings section that lists API keys. Create, Revoke and Delete show only where
 * the server says the caller may use them, so this page never reads roles.
 */
export function ApiKeysSection() {
  const { data: session } = useSession();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<KeyStatus | "all">("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const isPlatformAdmin = session?.user?.role === "admin";
  const activeMemberQuery = useActiveMemberQuery(!!session);
  const canCreateKeys = activeMemberQuery.data?.permissions.createApiKeys ?? false;
  const { data: apiKeys, isLoading } = useApiKeysQuery();

  const createKey = useCreateApiKeyMutation();
  const createdKey = createKey.data ?? null;
  const revokeKey = useRevokeApiKeyMutation();
  const deleteKey = useMutation(
    orpc.admin.deleteApiKey.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.admin.listApiKeys.key() });
      },
    })
  );

  const allKeys = apiKeys ?? [];
  const filteredKeys = allKeys.filter((k) => {
    const status = getApiKeyStatus(k);
    const matchesStatus = filterStatus === "all" || status === filterStatus;
    const matchesSearch =
      (k.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      k.keyPrefix.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (k.userEmail ?? "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const statusCounts = {
    all: allKeys.length,
    active: allKeys.filter((k) => getApiKeyStatus(k) === "active").length,
    expired: allKeys.filter((k) => getApiKeyStatus(k) === "expired").length,
    disabled: allKeys.filter((k) => getApiKeyStatus(k) === "disabled").length,
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">API Keys</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {isPlatformAdmin
              ? "View and manage all API keys across the platform."
              : "Create and manage your API keys for programmatic access."}
          </p>
        </div>
        {canCreateKeys && (
          <Button onClick={() => { createKey.reset(); setCopied(false); setShowCreateDialog(true); }}>
            <Key className="size-4 mr-2" />
            Create API Key
          </Button>
        )}
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search keys..."
              className="h-8 pl-8 text-xs"
            />
          </div>
          <div className="flex gap-1">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "expired", label: "Expired" },
              { key: "disabled", label: "Revoked" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterStatus(key as typeof filterStatus)}
                className={cn(
                  "px-2 py-1 text-xs font-medium rounded transition-colors",
                  filterStatus === key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {label} ({statusCounts[key as keyof typeof statusCounts]})
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredKeys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Key className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No API keys found</p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Key Prefix</th>
                  {isPlatformAdmin && (
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Owner</th>
                  )}
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Last Used</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Expires</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Created</th>
                  <th className="px-4 py-2.5 text-left font-medium w-28" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredKeys.map((key) => {
                  const status = getApiKeyStatus(key);
                  const statusConfig = getApiKeyStatusConfig(status);
                  const expiringSoon = isKeyExpiringSoon(key.expiresAt);
                  return (
                    <tr key={key.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="text-xs font-medium">{key.name || "Unnamed"}</div>
                        {(() => {
                          const meta = key.metadata as { type?: string } | null;
                          return meta?.type ? (
                            <div className="text-[10px] text-muted-foreground">{meta.type}</div>
                          ) : null;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded text-[10px] font-medium",
                            key.configId === "sdk-api"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                              : "bg-muted text-muted-foreground"
                          )}
                        >
                          {key.configId === "sdk-api" ? "SDK" : "Mobile"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded">
                          {key.keyPrefix}••••
                        </code>
                      </td>
                      {isPlatformAdmin && (
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium">{key.userName}</div>
                          <div className="text-[10px] text-muted-foreground">{key.userEmail}</div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "px-2 py-0.5 rounded text-[10px] font-medium",
                            statusConfig.bgColor,
                            statusConfig.color
                          )}
                        >
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground">
                          {formatKeyRelativeTime(key.lastUsed)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {key.expiresAt ? (
                          <div className="flex items-center gap-1">
                            {expiringSoon && (
                              <AlertTriangle className="h-3 w-3 text-orange-500" />
                            )}
                            <span
                              className={cn(
                                "text-xs",
                                expiringSoon ? "text-orange-500" : "text-muted-foreground"
                              )}
                            >
                              {formatKeyDateTime(key.expiresAt)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatKeyRelativeTime(key.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {key.canManage && (
                          <div className="flex gap-1">
                            {status === "active" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => revokeKey.mutate({ keyId: key.id })}
                                disabled={revokeKey.isPending}
                                className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                Revoke
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (confirm("Delete this API key permanently?")) {
                                  deleteKey.mutate({ keyId: key.id });
                                }
                              }}
                              disabled={deleteKey.isPending}
                              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create API Key Dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setNewKeyName("");
            setCopied(false);
            createKey.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdKey ? "API Key Created" : "Create API Key"}</DialogTitle>
            <DialogDescription>
              {createdKey
                ? "Copy your API key now. You won't be able to see it again."
                : "Create a new API key for programmatic access. Give it a descriptive name."}
            </DialogDescription>
          </DialogHeader>
          {createdKey ? (
            <div className="flex items-center gap-2 rounded-lg border bg-muted/30 py-1.5 pl-3 pr-1.5">
              <input
                readOnly
                value={createdKey.key}
                aria-label="API key"
                className="flex-1 min-w-0 bg-transparent text-sm font-mono outline-none"
              />
              <Button
                size="icon-sm"
                variant="outline"
                className="shrink-0"
                aria-label={copied ? "Copied" : "Copy API key"}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(createdKey.key);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  } catch {
                    setCopied(false);
                  }
                }}
              >
                <span className="relative inline-flex h-4 w-4 items-center justify-center">
                  <Copy
                    aria-hidden
                    className={cn(
                      "absolute size-4 transition-all duration-200",
                      copied ? "scale-50 opacity-0" : "scale-100 opacity-100",
                    )}
                  />
                  <Check
                    aria-hidden
                    className={cn(
                      "absolute size-4 text-green-600 dark:text-green-400 transition-all duration-200",
                      copied ? "scale-100 opacity-100" : "scale-50 opacity-0",
                    )}
                  />
                </span>
              </Button>
            </div>
          ) : (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="api-key-name">Name</FieldLabel>
                <Input
                  id="api-key-name"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g. Production API"
                />
              </Field>
              {createKey.isError && (
                <p className="text-sm text-destructive">{createKey.error.message}</p>
              )}
            </FieldGroup>
          )}
          <DialogFooter>
            {createdKey ? (
              <Button onClick={() => { setShowCreateDialog(false); setNewKeyName(""); setCopied(false); createKey.reset(); }}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => createKey.mutate({ name: newKeyName.trim() || "API Key" })}
                  disabled={createKey.isPending || !newKeyName.trim() || createKey.isPending}
                >
                  {createKey.isPending ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
